<template>
    <div class="text-slate-800 p-4 max-w-full sm:min-w-[480px] lg:p-6">
        <div
            class="rounded-3xl p-2 mb-6"
            :class="{
                'bg-slate-100': !search.length,
                'bg-sky-100': search.length,
            }"
        >
            <div class="shadow rounded-xl">
                <vx-input
                    size="lg"
                    v-model="search"
                    maxlength="40"
                    ref="searchInputRef"
                    placeholder="Search"
                    block
                    :aria-activedescendant="toggledOptions[activeIndex]?.id || toggledOptions[activeIndex]?.group_id"
                    @keydown="onListKeyDown"
                >
                    <template v-slot:right>
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </div>

        <component
            :is="onCorporate() ? 'div' : 'span'"
            v-if="(canAccessCorporate || availableOrgUnit) && !search.length"
            ref="corporateRef"
            @click="canAccessCorporate ? switchToCorporate() : switchOption(availableOrgUnit)"
            @keydown.enter="canAccessCorporate ? switchToCorporate() : switchOption(availableOrgUnit)"
            @focus="activeIndex = -1"
            tabindex="0"
            :aria-selected="activeIndex === -1"
            class="text-slate-700! hover:no-underline hover:bg-sky-100 focus:bg-sky-100 flex justify-between items-center py-2.5 px-3 mb-4 rounded-lg border border-solid border-slate-300 cursor-pointer outline-hidden"
            :class="{
                'bg-sky-100': activeIndex === -1,
            }"
        >
            <template v-if="canAccessCorporate">
                <div class="flex gap-2 items-center flex-row">
                    <vx-radio
                        :modelValue="(onCorporate() && selectedIndex == undefined) || selectedIndex === -1"
                    ></vx-radio>
                    <span class="text-sm font-bold">View as Corporate</span>
                </div>
                <vx-badge color="amber" size="xs">
                    Corporate
                    <font-awesome-icon widthAuto :icon="faCrown" class="text-amber-500"></font-awesome-icon>
                </vx-badge>
            </template>
            <template v-else>
                <div class="flex gap-2 items-center flex-row">
                    <vx-radio
                        :modelValue="currentOrgUnit?.id === availableOrgUnit.id || selectedIndex === -1"
                    ></vx-radio>
                    <span class="text-sm font-bold">View as {{ availableOrgUnit.name }}</span>
                </div>
                <vx-badge color="violet" size="xs">
                    Org
                    <font-awesome-icon widthAuto :icon="faBuildingFlag" class="text-violet-500"></font-awesome-icon>
                </vx-badge>
            </template>
        </component>

        <div v-if="!search.length" class="font-extrabold mb-2 text-xs">Recents</div>
        <div
            role="listbox"
            class="flex flex-col"
            :class="{
                'gap-2': search.length,
            }"
        >
            <div v-if="!filteredOptions.length && search" class="text-slate-500 text-center -mt-2 text-sm">
                No results found with "{{ search }}"
            </div>
            <div v-if="!filteredOptions.length && !search" class="text-slate-500 text-center -mt-2 text-sm">
                No groups or org units yet.
            </div>
            <component
                :is="isCurrentOption(toggledOption) ? 'div' : 'span'"
                v-for="(toggledOption, index) in toggledOptions"
                :id="'context_switch_' + (toggledOption.id || toggledOption.group_id)"
                :key="'context_switch_' + (toggledOption.id || toggledOption.group_id)"
                role="option"
                :aria-selected="index === activeIndex ? true : false"
                tabindex="0"
                :ref="(el) => (optionsRef[index] = el)"
                @click="switchOption(toggledOption)"
                @keydown.enter="switchOption(toggledOption)"
                @focus="activeIndex = index"
                @mouseenter="
                    () => {
                        if (disableMouseIndexChange) {
                            return;
                        }
                        activeIndex = index;
                    }
                "
                @mousemove="disableMouseIndexChange = false"
                class="text-slate-700! hover:no-underline flex items-center justify-between rounded-sm py-2 px-3 cursor-pointer outline-hidden"
                :class="{
                    'bg-sky-100': activeIndex === index,
                    'border border-solid border-slate-100': search.length,
                }"
            >
                <div class="flex items-center gap-2 flex-row">
                    <vx-radio
                        :modelValue="
                            (isCurrentOption(toggledOption) && selectedIndex == undefined) || selectedIndex === index
                        "
                    ></vx-radio>
                    <div>
                        <div class="text-sm text-slate-700! font-bold line-clamp-1">
                            <highlighter
                                :searchWords="searchHighlight"
                                :autoEscape="true"
                                highlightClassName="text-amber-600 p-0 bg-transparent"
                                :textToHighlight="toggledOption.name"
                            >
                            </highlighter>
                        </div>
                        <div class="text-xs text-slate-500! break-all">
                            <highlighter
                                :searchWords="searchHighlight"
                                :autoEscape="true"
                                :caseSensitive="true"
                                highlightClassName="text-amber-600 p-0 bg-transparent"
                                :textToHighlight="toggledOption.type === 'group' ? toggledOption.group_id : ''"
                            >
                            </highlighter>
                        </div>
                    </div>
                </div>
                <vx-badge v-if="toggledOption.type === 'orgUnit'" color="violet" size="xs">
                    Org
                    <font-awesome-icon widthAuto :icon="faBuildingFlag" class="text-violet-500"></font-awesome-icon>
                </vx-badge>
                <vx-badge v-if="toggledOption.type === 'group'" color="slate" size="xs">
                    Group
                    <font-awesome-icon widthAuto :icon="faGrid2Plus" class="text-slate-500"></font-awesome-icon>
                </vx-badge>
            </component>
            <vx-button
                v-if="(options.length > 8 && !search.length) || (filteredOptions.length > 8 && search.length)"
                role="option"
                class="mt-2"
                color="muted"
                @click="toggleOptions"
                tabindex="0"
            >
                {{ showAll ? 'Show Less' : 'Load More' }}
            </vx-button>
        </div>
    </div>
</template>

<script setup>
import { VxRadio, VxButton, VxInput, VxBadge } from '@voxie/frontend-components';
import { faBuildingFlag, faCrown, faGrid2Plus, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import Highlighter from 'vue-highlight-words';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import axios from 'axios';

const emit = defineEmits(['switching']);
const teamId = Spark.state.currentTeam.id;

const searchInputRef = ref();

const showAll = ref(false);
const search = ref('');
const searchHighlight = computed(() => [search.value]);

const { onCorporate, availableGroups, availableOrgUnits, currentGroup, currentOrgUnit, canAccessCorporate } =
    useCurrentGroup();

onMounted(() => {
    searchInputRef.value?.field?.focus();
});

const availableOrgUnit = computed(() => {
    return availableOrgUnits.value.length === 1
        ? {
              ...availableOrgUnits.value[0],
              type: 'orgUnit',
          }
        : null;
});

const options = computed(() => {
    const groupsWithType = availableGroups.value.map((group) => ({
        ...group,
        type: 'group',
    }));

    // only show groups list if there's only a single org unit available
    // and doesnt have access to corporate
    if (availableOrgUnit.value && !canAccessCorporate.value) {
        return groupsWithType;
    }

    const orgUnitsWithType = availableOrgUnits.value.map((orgUnit) => ({
        ...orgUnit,
        type: 'orgUnit',
    }));

    const combined = [...groupsWithType, ...orgUnitsWithType];

    return combined.sort((a, b) => {
        // Prioritize the current selection at the top
        const aIsCurrent = isCurrentOption(a);
        const bIsCurrent = isCurrentOption(b);

        if (aIsCurrent && !bIsCurrent) {
            return -1;
        }

        if (!aIsCurrent && bIsCurrent) {
            return 1;
        }

        return a.name.localeCompare(b.name);
    });
});

const filteredOptions = computed(() => {
    if (!search.value.length) {
        return options.value;
    }

    return options.value.filter((option) => {
        if (option.name.toLowerCase().includes(search.value.toLowerCase())) {
            return true;
        }

        return option.type === 'group' && option.group_id.includes(search.value);
    });
});

const isCurrentOption = (option) => {
    if (option.type === 'group') {
        return currentGroup.value?.group_id === option.group_id;
    } else if (option.type === 'orgUnit') {
        return currentOrgUnit.value?.id === option.id;
    }

    return false;
};

const switchingOption = ref(false);

const handleContextSwitch = (request) => {
    request
        .then(() => {
            if (window.location.pathname.startsWith('/message-hub')) {
                window.location.replace('/message-hub');
            } else {
                window.location.reload();
            }
        })
        .finally(() => {
            switchingOption.value = false;
        });
};

const switchOption = async (toggledOption) => {
    if (switchingOption.value) {
        return;
    }

    switchingOption.value = true;

    if (!isCurrentOption(toggledOption)) {
        emit('switching');
        selectedIndex.value = activeIndex.value;

        let switchUrl;
        if (toggledOption.type === 'group') {
            switchUrl = `/settings/teams/${teamId}/groups/${toggledOption.group_id}/switch`;
        } else if (toggledOption.type === 'orgUnit') {
            switchUrl = `/settings/teams/${teamId}/org-units/${toggledOption.id}/switch`;
        }

        handleContextSwitch(axios.get(switchUrl));
    }
};

const switchToCorporate = async () => {
    emit('switching');
    selectedIndex.value = -1;
    handleContextSwitch(axios.get(`/settings/teams/${teamId}/switch`));
};

const toggledOptions = computed(() => (showAll.value ? filteredOptions.value : filteredOptions.value.slice(0, 8)));

const toggleOptions = async () => {
    showAll.value = !showAll.value;
    await nextTick();
    searchInputRef.value?.field?.focus();

    if (activeIndex.value > toggledOptions.value.length - 1) {
        activeIndex.value = 0;
    }
};

// -1 is view as corporate
const getInitialIndex = () => (onCorporate() ? 0 : canAccessCorporate.value ? -1 : 0);
const activeIndex = ref(getInitialIndex());

// used when enter is pressed
const selectedIndex = ref();
const disableMouseIndexChange = ref(false);

const getNextIndex = (current, direction) => {
    const initialIndex = getInitialIndex();
    const max = toggledOptions.value.length - 1;
    if (direction === 'down') {
        if (current < max) {
            return current + 1;
        }
        if (current === max) {
            return initialIndex;
        }
        if (current === -1) {
            return 0;
        }
    } else if (direction === 'up') {
        if (current > 0) {
            return current - 1;
        }
        if (current === 0) {
            return initialIndex;
        }
        if (current === -1) {
            return max;
        }
    }
    return current;
};

const onListKeyDown = (event) => {
    if (!toggledOptions.value.length && !canAccessCorporate.value) {
        return;
    }

    if (event.key === 'ArrowDown') {
        disableMouseIndexChange.value = true;
        event.preventDefault();
        if (activeIndex.value + 1 === toggledOptions.value.length - 1 && !showAll.value) {
            showAll.value = true;
        }

        activeIndex.value = getNextIndex(activeIndex.value, 'down');
        scrollToActive();
    } else if (event.key === 'ArrowUp') {
        disableMouseIndexChange.value = true;
        event.preventDefault();
        activeIndex.value = getNextIndex(activeIndex.value, 'up');
        scrollToActive();
    } else if (event.key === 'Enter') {
        event.preventDefault();
        if (activeIndex.value === -1 && canAccessCorporate.value && !search.value.length) {
            switchToCorporate();
        } else if (activeIndex.value >= 0) {
            switchOption(toggledOptions.value[activeIndex.value]);
        }
    }
};

const corporateRef = ref([]);
const optionsRef = ref([]);
const isElementInViewport = (el, offset = 50) => {
    const rect = el.getBoundingClientRect();
    return rect.top >= 0 + offset && rect.bottom <= window.innerHeight - offset;
};

const scrollToActive = async () => {
    if (!optionsRef.value) {
        return;
    }

    const el = activeIndex.value === -1 ? corporateRef.value : optionsRef.value[activeIndex.value];
    if (el && el?.scrollIntoView && !isElementInViewport(el)) {
        el.scrollIntoView({ block: 'center' });
    }
};

watch(search, async (search) => {
    activeIndex.value = search.length ? 0 : getInitialIndex();
    await nextTick();
    scrollToActive();
});
</script>
